import { useState } from "react";

import { Bell } from "lucide-react";

import { NotificationList } from "src/components/Notifications/NotificationList";
import { Button } from "src/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "src/components/ui/drawer";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import { useIsMobile } from "src/lib/hooks/useMobile";
import { useNotifications } from "src/lib/hooks/useNotifications";

export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const {
    activeNotifications,
    historyNotifications,
    historyCount,
    unreadCount,
    refetchAll,
    markNotificationRead,
  } = useNotifications();

  const handleOpenChange = (nextOpen) => {
    setOpen(nextOpen);

    if (nextOpen) {
      refetchAll();
    }
  };

  const triggerButton = (
    <Button variant="ghost" size="icon" className="relative">
      <Bell className="size-4" />
      {unreadCount > 0 && (
        <span className="bg-primary text-primary-foreground absolute top-0 right-0 inline-flex min-w-4 min-h-4 items-center justify-center rounded-full px-1 text-xs font-semibold">
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      )}
      <span className="sr-only">Notifications</span>
    </Button>
  );

  const notificationList = (
    <NotificationList
      activeNotifications={activeNotifications}
      historyNotifications={historyNotifications}
      historyCount={historyCount}
      compact={!isMobile}
      showActiveEmptyState
      collapseHistoryByDefault
      onAction={() => setOpen(false)}
      onMarkRead={async (id) => {
        await markNotificationRead({ id });
      }}
    />
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={handleOpenChange}>
        <DrawerTrigger asChild>{triggerButton}</DrawerTrigger>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Notifications</DrawerTitle>
            <DrawerDescription className="sr-only">
              View active notifications and recent notification history.
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-4">{notificationList}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>

      <PopoverContent
        align="end"
        side="bottom"
        className="w-[min(32rem,calc(100vw-2rem))] space-y-4"
      >
        <div className="text-sm font-semibold">Notifications</div>
        {notificationList}
      </PopoverContent>
    </Popover>
  );
}
